import { useState, useEffect, useMemo } from 'react';
import { Drawer, Button } from '@aimsos/ui';
import { getShellMainContentEl } from '../../../../shared/utils/shellPortal.js';
import InitialToolsView from './InitialToolsView.jsx';
import ToolConfigureFormPanel from './ToolConfigureFormPanel.jsx';

const SlideoutTools = ({
  t,
  open,
  onClose,
  onAddTools,
  onToolConfiguration,
  installedTools = [],
  availableTools = [],
  isLoading = false,
  /** When set, same drawer shows configure form instead of the catalog. */
  libraryConfigureTool = null,
  onLibraryConfigureBack,
  onLibraryConfigureSave,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTools, setSelectedTools] = useState([]);

  const drawerPortalTarget = useMemo(
    () => (open ? getShellMainContentEl() : null),
    [open],
  );

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      setSearchQuery('');
      setSelectedTools([]);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  const handleSearchChange = (value) => {
    const searchValue = typeof value === 'string' ? value : value?.target?.value || '';
    setSearchQuery(searchValue);
  };

  const handleResetSelections = () => {
    setSelectedTools([]);
  };

  const handleToolSelection = (action_uuid) => {
    setSelectedTools((prev) => {
      if (prev.includes(action_uuid)) return prev.filter((id) => id !== action_uuid);
      return [...prev, action_uuid];
    });
  };

  const selectedCount = selectedTools.length;

  const handleToolConfigurationFromLibrary = (tool) => {
    if (onToolConfiguration) onToolConfiguration(tool, true);
  };

  const transformedAvailableTools = availableTools.map((tool) => {
    const id = tool.action_uuid || tool.tool_id;
    const name = tool.name || tool.title;
    const category = tool.category || tool.categories || '';

    return {
      action_uuid: id,
      tool_id: id,
      id,
      name,
      title: name,
      label: name,
      description: tool.description || '',
      type: tool.type || 'action',
      categories: Array.isArray(category) ? category : category ? [category] : [],
      icon: tool.icon || '',
      tag: tool.tag || 'Ready to use',
      tagColor: tool.tagColor || 'informative',
      required_parameters: tool.required_parameters || [],
    };
  });

  const installedToolIds = useMemo(
    () => new Set(installedTools.map((tool) => tool.id)),
    [installedTools],
  );

  const filterTools = (tools) =>
    tools.filter((tool) => {
      const toolId = tool.id || tool.action_uuid || tool.tool_id;
      const isNotInstalled = !installedToolIds.has(toolId);

      const q = (searchQuery || '').toLowerCase();
      const text = [tool?.name, tool?.label, tool?.title, tool?.description]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const bySearch = !q || text.includes(q);
      return isNotInstalled && bySearch;
    });

  const filteredAvailableTools = filterTools(transformedAvailableTools);

  const tools = filteredAvailableTools.map((item) => ({
    ...item,
    id: item.id || item.action_uuid || item.tool_id,
    label: item.name || item.label,
    title: item.name || item.title,
    categories: Array.isArray(item.categories)
      ? item.categories
      : item.categories
        ? [item.categories]
        : [],
  }));

  const handleAddToolsClick = () => {
    if (onAddTools && selectedTools.length > 0) {
      const selectedToolObjects = tools.filter((tool) => selectedTools.includes(tool.id));
      onAddTools(selectedToolObjects);
    }
    handleResetSelections();
    onClose();
  };

  const configureTitle =
    libraryConfigureTool &&
    (libraryConfigureTool.title || libraryConfigureTool.name || t('edit.tools.tool'));

  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      portalTarget={drawerPortalTarget ?? undefined}
      widthClassName="w-[480px] shrink-0"
      panelClassName="min-w-0"
      ariaLabel={
        libraryConfigureTool
          ? t('edit.tools.configureTitle', { title: configureTitle })
          : t('edit.tools.slideoutTitle')
      }
    >
      <div className="flex h-full min-h-0 flex-col bg-surface p-6">
        {libraryConfigureTool ? (
          <ToolConfigureFormPanel
            t={t}
            tool={libraryConfigureTool}
            showBackButton
            onBack={onLibraryConfigureBack}
            onSave={(formData) => onLibraryConfigureSave?.(libraryConfigureTool, formData)}
          />
        ) : isLoading ? (
          <div className="flex flex-1 items-center justify-center text-sm text-content-muted">
            {t('edit.tools.loadingCatalog')}
          </div>
        ) : (
          <>
            <InitialToolsView
              t={t}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              tools={tools}
              selectedTools={selectedTools}
              onToolSelection={handleToolSelection}
              onToolConfiguration={handleToolConfigurationFromLibrary}
            />

            <div className="mt-auto flex justify-end gap-2 border-t border-edge pt-4">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={handleResetSelections}
                disabled={selectedCount === 0}
              >
                {t('edit.tools.deselectAll')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={handleAddToolsClick}
                disabled={selectedCount === 0}
              >
                {t('edit.tools.addCount', { count: selectedCount })}
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default SlideoutTools;
